// src/pages/PaymentPage.tsx

import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import {
  PAYMENT_STRINGS,
  COMMON_STRINGS,
} from "../../constants/strings";

import { getPayments } from "../../services/api";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

// ✅ TYPE
import { PaymentRow } from "../../types/payment";

const PaymentPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    PaymentRow["status"] | ""
  >("");

  // ================= FETCH =================
  const fetchPayments = async () => {
    try {
      setLoading(true);

      const res = await getPayments();
      console.log("PAYMENTS:", res);

      // ✅ correct extraction
      const apiData =
        res?.data?.data || res?.data || [];

      // 🔥 FORMAT (UPDATED)
      const formatted: PaymentRow[] = apiData.map(
        (item: any, index: number) => ({
          id: item._id || String(index),

          paymentId: item._id?.slice(-6) || "N/A",

          user:
            item.user?.name?.trim() ||
            PAYMENT_STRINGS.GUEST_USER,

          service: item.role || "-", // 🔥 provider/customer role

          amount: item.amount || 0,

          date: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-IN")
            : "-",

          time: item.createdAt
            ? new Date(item.createdAt).toLocaleTimeString("en-IN")
            : "-",

          status: item.status || "pending", // success / pending / failed
        })
      );

      setRows(formatted);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message:
            err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();

    const matchSearch =
      row.paymentId.toLowerCase().includes(search) ||
      row.user.toLowerCase().includes(search) ||
      row.service.toLowerCase().includes(search);

    const matchStatus = statusFilter
      ? row.status === statusFilter
      : true;

    return matchSearch && matchStatus;
  });

  // ================= COLUMNS =================
  const columns: GridColDef<PaymentRow>[] = [
    {
      field: "paymentId",
      headerName: PAYMENT_STRINGS.COLUMN_ID,
      flex: 1,
    },
    {
      field: "user",
      headerName: PAYMENT_STRINGS.COLUMN_USER,
      flex: 1,
    },
    {
      field: "service",
      headerName: "Role", // 🔥 updated label
      flex: 1,
    },
    {
      field: "amount",
      headerName: PAYMENT_STRINGS.COLUMN_AMOUNT,
      flex: 1,
      renderCell: (params) => `₹${params.value}`,
    },
    {
      field: "date",
      headerName: PAYMENT_STRINGS.COLUMN_DATE,
      flex: 1,
    },
    {
      field: "time",
      headerName: PAYMENT_STRINGS.COLUMN_TIME,
      flex: 1,
    },
    {
      field: "status",
      headerName: PAYMENT_STRINGS.COLUMN_STATUS,
      flex: 1,
      renderCell: (params) => (
        <StatusChip status={params.value} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={PAYMENT_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={PAYMENT_STRINGS.SEARCH_PLACEHOLDER}
        filterValue={statusFilter}
        onFilterChange={(value) =>
          setStatusFilter(value as PaymentRow["status"] | "")
        }
        filterOptions={[
          { label: "All Status", value: "" },
          { label: "Success", value: "success" },
          { label: "Pending", value: "pending" },
          { label: "Failed", value: "failed" },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id}
        pageSizeOptions={[10, 20, 50]}
      />
    </Box>
  );
};

export default PaymentPage;